/**
 * Assets API service with TanStack React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut, apiDelete, isTokenGetterSet } from './client'
import type { GoldAsset, CreateGoldAssetInput, UpdateGoldAssetInput } from '@/types/asset'

/**
 * Query keys for assets
 */
export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  list: (filters?: { status?: 'holding' | 'sold' }) => [...assetKeys.lists(), filters] as const,
  details: () => [...assetKeys.all, 'detail'] as const,
  detail: (id: string) => [...assetKeys.details(), id] as const,
}

/**
 * Fetch all assets for the current user
 */
export function useAssets(status?: 'holding' | 'sold') {
  return useQuery({
    queryKey: assetKeys.list(status ? { status } : undefined),
    queryFn: async () => {
      const params = status ? `?status=${status}` : ''
      const response = await apiGet<{ data: GoldAsset[] }>(`/api/assets${params}`)
      return response.data
    },
    enabled: isTokenGetterSet(), // Only run when token getter is set
  })
}

/**
 * Fetch a single asset by ID
 */
export function useAsset(id: string) {
  return useQuery({
    queryKey: assetKeys.detail(id),
    queryFn: async () => {
      const response = await apiGet<{ data: GoldAsset }>(`/api/assets/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

/**
 * Create a new asset
 */
export function useCreateAsset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateGoldAssetInput) => {
      const response = await apiPost<{ data: GoldAsset; message: string }>('/api/assets', input)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch assets list
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },
  })
}

/**
 * Update an existing asset
 */
export function useUpdateAsset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateGoldAssetInput }) => {
      const response = await apiPut<{ data: GoldAsset; message: string }>(`/api/assets/${id}`, input)
      return response.data
    },
    onSuccess: (data) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(data.id) })
    },
  })
}

/**
 * Delete an asset
 */
export function useDeleteAsset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete<{ message: string }>(`/api/assets/${id}`)
      return id
    },
    onSuccess: () => {
      // Invalidate and refetch assets list
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },
  })
}

